import React, { useEffect, useState, useMemo } from 'react';
import api from '../api/client';
import { Plus, X, Flag, Loader2, Trash2, AlertOctagon, MessageSquarePlus } from 'lucide-react';
import CsvImport from '../components/app/CsvImport';
import SearchBar, { matches } from '../components/app/SearchBar';

const REASON_LABELS = {
  propietario_descontento: 'Propietario descontento',
  incidencias_recurrentes: 'Incidencias recurrentes',
  riesgo_no_renovacion: 'Riesgo no renovación',
  impago: 'Impago',
  queja_comunidad: 'Queja comunidad',
  acceso_denegado: 'Acceso denegado',
  otro: 'Otro',
};
const SEV = {
  alta: 'bg-red-500/20 text-red-300 border-red-500/40',
  media: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  baja: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
};

const Conflictivos = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get('/conflictive'), api.get('/sites/lite')]);
      setItems(c.data); setSites(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  const filtered = useMemo(() => items.filter((c) => matches(search,
    c.description, REASON_LABELS[c.reason], c.severity,
    siteMap[c.site_id]?.name, siteMap[c.site_id]?.municipality, siteMap[c.site_id]?.address,
  )), [items, search, siteMap]);

  const del = async (id) => {
    if (!confirm('¿Eliminar este registro?')) return;
    await api.delete(`/conflictive/${id}`);
    await load();
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="conflictive-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">MANTENIMIENTO · RIESGO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Emplazamientos conflictivos</h1>
          <p className="text-white/50 text-sm mt-1">Sites con bandera roja: propietario, contrato o incidencias.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport entity="conflictive" onImported={load}/>
          <button onClick={() => setShowModal(true)} data-testid="btn-new-conflict"
            className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#8ee00a] text-sm">
            <Plus size={16}/> Marcar site
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <Flag className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50">Sin emplazamientos conflictivos.</p>
        </div>
      ) : (
        <>
          <div className="mb-3"><SearchBar value={search} onChange={setSearch} placeholder="Buscar por site, motivo o descripción…" testId="conflict-search"/></div>
          {filtered.length === 0 && (
            <div className="text-center py-10 text-white/40 text-sm">Ningún emplazamiento coincide con «{search}».</div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {filtered.map((c) => (
              <div key={c.id} onClick={() => setDetailId(c.id)} data-testid={`conflict-${c.id}`}
                className="bg-black/30 border border-white/10 hover:border-red-500/40 rounded-2xl p-4 cursor-pointer transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Flag size={14} className="text-red-400"/>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${SEV[c.severity]}`}>{c.severity.toUpperCase()}</span>
                      <span className="text-[10px] uppercase text-white/50 bg-white/5 px-2 py-0.5 rounded-full">{REASON_LABELS[c.reason]}</span>
                    </div>
                    <div className="text-white font-bold truncate">{siteMap[c.site_id]?.name || 'Site desconocido'}</div>
                    <div className="text-white/50 text-xs mt-1">{c.description}</div>
                    <div className="text-white/40 text-xs mt-2 flex gap-3 flex-wrap">
                      {c.contract_end && <span>📅 Vence: {c.contract_end}</span>}
                      <span>Riesgo renov.: <span className="text-red-400 font-bold">{c.renewal_risk_pct?.toFixed(0)}%</span></span>
                      <span>{c.events?.length || 0} eventos</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showModal && <ConflictModal sites={sites} onClose={() => setShowModal(false)} onSaved={async () => { setShowModal(false); await load(); }}/>}
      {detailId && <ConflictDetail id={detailId} siteMap={siteMap} onClose={() => setDetailId(null)} onChanged={load}/>}
    </div>
  );
};

const ConflictModal = ({ sites, onClose, onSaved }) => {
  const [form, setForm] = useState({
    site_id: sites[0]?.id || '',
    reason: 'propietario_descontento',
    severity: 'media',
    contract_end: '',
    renewal_risk_pct: 50,
    description: '',
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/conflictive', form);
      onSaved();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="conflict-modal"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex justify-between">
          <h3 className="text-lg font-black flex items-center gap-2"><AlertOctagon className="text-red-400" size={18}/> Marcar como conflictivo</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs uppercase text-white/60">Emplazamiento</span>
            <select required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
              <option value="">Selecciona...</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Motivo</span>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Severidad</span>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                <option value="alta">🔴 Alta</option>
                <option value="media">🟠 Media</option>
                <option value="baja">🟡 Baja</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Vto. contrato</span>
              <input type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Riesgo no renov. %</span>
              <input type="number" min="0" max="100" value={form.renewal_risk_pct} onChange={(e) => setForm({ ...form, renewal_risk_pct: parseFloat(e.target.value) || 0 })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Descripción</span>
            <textarea rows="3" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="btn-save-conflict"
            className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ConflictDetail = ({ id, siteMap, onClose, onChanged }) => {
  const [c, setC] = useState(null);
  const [ev, setEv] = useState('');
  const load = async () => {
    const { data } = await api.get('/conflictive');
    const f = data.find((x) => x.id === id);
    setC(f);
  };
  useEffect(() => { load(); }, [id]);

  const addEvent = async () => {
    if (!ev.trim()) return;
    await api.post(`/conflictive/${id}/events`, { summary: ev });
    setEv('');
    await load(); onChanged?.();
  };

  if (!c) return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"><Loader2 className="animate-spin text-[#A3FF12]"/></div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Flag className="text-red-400" size={16}/>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${SEV[c.severity]}`}>{c.severity.toUpperCase()}</span>
              <span className="text-[10px] uppercase text-white/50">{REASON_LABELS[c.reason]}</span>
            </div>
            <h3 className="text-xl font-black">{siteMap[c.site_id]?.name || 'Site'}</h3>
            <p className="text-white/60 text-sm mt-1">{c.description}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="text-xs uppercase text-white/50 font-bold mb-2">Eventos / historial</div>
          <div className="space-y-2 mb-4">
            {(c.events || []).length === 0 && <p className="text-white/40 text-sm">Sin eventos registrados.</p>}
            {(c.events || []).slice().reverse().map((e) => (
              <div key={e.id} className="bg-black/40 border border-white/5 rounded-lg p-3">
                <div className="text-white/40 text-[10px] uppercase">{new Date(e.date).toLocaleString('es-ES')}</div>
                <div className="text-white/80 text-sm">{e.summary}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={ev} onChange={(e) => setEv(e.target.value)} placeholder="Nuevo evento (llamada, visita, propuesta, …)"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="conflict-event-input"/>
            <button onClick={addEvent} data-testid="btn-add-conflict-event"
              className="bg-[#A3FF12] text-black font-bold px-3 py-2 rounded-lg text-sm flex items-center gap-1"><MessageSquarePlus size={14}/> Añadir</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Conflictivos;
